import styled from '@emotion/styled';
import React, { forwardRef, memo, useCallback, useEffect, useState } from 'react';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useResizeCallback from '@polestar/component-warehouse-react/hooks/useResizeCallback';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Breakpoints from '@polestar/component-warehouse-react/tokens/breakpoints/Breakpoints';
import AspectRatio from '@polestar/component-warehouse-react/components/aspect-ratio/AspectRatio';
const VideoPlayerSubtitles = memo(forwardRef(({ selectedSubtitle, textTracks, controlsVisible, visible = false, videoPlayerDimensions, ...props }, ref) => {
    const [text, setText] = useState();
    const [videoWidth, setVideoWidth] = useState(0);
    const handleCueChange = useCallback((e) => {
        const cues = e.currentTarget.activeCues;
        const list = [];
        for (const cue of cues) {
            list.push(cue.text);
        }
        setText(list.length > 0 ? list : undefined);
    }, []);
    // determine if an y-position adjustment is needed when for example
    // the video is showing black bars due to a ratio difference or
    // fullscreen when there is not enough space to show the video exactly
    const calcRatio = videoPlayerDimensions.width > 0
        ? videoPlayerDimensions.width / videoPlayerDimensions.video.width
        : 0;
    const diffHeight = Math.round((videoPlayerDimensions.height - videoPlayerDimensions.video.height * calcRatio) * 0.5);
    // calculate font size & spacings based on video width
    const videoPlayerNumber = useMotionValue(0);
    const fontSize = useTransform(videoPlayerNumber, [375, Breakpoints.desktop], ['16px', '30px']);
    const lineHeight = useTransform(videoPlayerNumber, [375, Breakpoints.desktop], ['18px', '32px']);
    const sideSpacing = useTransform(videoPlayerNumber, [375, Breakpoints.desktopWide], ['16px', '48px']);
    const bottomSpacing = useTransform(videoPlayerNumber, [Breakpoints.tablet, Breakpoints.desktop], [Spacings.medium, Spacings.xxLarge]);
    // ensure px values are used
    const bottomSpacingValue = useTransform(bottomSpacing, (latestBottomSpacing) => `translateY(
        calc(
          -${latestBottomSpacing} +
          min(
            calc(${diffHeight}px - ${controlsVisible ? Spacings.xxxLarge : '0px'}), 0px
          )
        )
       )`);
    useEffect(() => {
        videoPlayerNumber.set(videoWidth);
    }, [videoWidth, videoPlayerNumber]);
    const internalRef = useResizeCallback((node) => {
        if (node) {
            const { width } = node === null || node === void 0 ? void 0 : node.getBoundingClientRect();
            setVideoWidth(width);
        }
    }, []);
    const combinedRef = useCombinedRef(ref, internalRef);
    useEffect(() => {
        if (textTracks && selectedSubtitle !== undefined && selectedSubtitle !== -1) {
            textTracks === null || textTracks === void 0 ? void 0 : textTracks[selectedSubtitle].addEventListener('cuechange', handleCueChange);
            return () => {
                textTracks === null || textTracks === void 0 ? void 0 : textTracks[selectedSubtitle].removeEventListener('cuechange', handleCueChange);
            };
        }
    }, [selectedSubtitle, handleCueChange, textTracks]);
    return (React.createElement(Container, null,
        React.createElement(AspectRatio, { ratio: videoPlayerDimensions.video.width / videoPlayerDimensions.video.height },
            React.createElement(Wrapper, { ...props, ref: combinedRef, style: {
                    insetInlineStart: sideSpacing,
                    insetInlineEnd: sideSpacing,
                    transform: bottomSpacingValue,
                    opacity: visible ? 1 : 0,
                }, "data-type": "subtitles" }, text === null || text === void 0 ? void 0 : text.map((line, index) => (React.createElement(Subtitle, { key: index, style: {
                    fontSize,
                    lineHeight,
                } }, line)))))));
}));
VideoPlayerSubtitles.displayName = 'VideoPlayerSubtitles';
export default VideoPlayerSubtitles;
const Container = styled.div `
  pointer-events: none;
  position: absolute;
  display: flex;
  align-items: center;
  overflow: hidden;
  inset-inline-start: 0;
  inset-block-start: 0;
  width: 100%;
  height: 100%;
`;
const Wrapper = styled(motion.div) `
  position: absolute;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex-direction: column;
  padding-block-end: 0;
  inset-block-start: 0;
  inset-block-end: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  transition: ${createCSSTransition('transform', MotionEasings.default, MotionDurations.slow)};

  /* !needs to be below the controls! */
  z-index: 1;
`;
/**
 * be aware that `text-stroke` is not an option, while you cannot adjust the direction of the stroke,
 * it will `inset` and make the text unreadable
 *
 * the thickness cannot go bigger while then gaps will appear
 */
const thickness = '2px';
const color = `rgba(0,0,0,0.2)`;
const Subtitle = styled(motion.span) `
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  color: white;
  width: 100%;
  min-height: 1rem;
  text-align: center;
  text-shadow:
    ${thickness} ${thickness} 0px ${color},
    -${thickness} -${thickness} 0px ${color},
    ${thickness} -${thickness} 0px ${color},
    -${thickness} ${thickness} 0px ${color},
    -${thickness} 0 0px ${color},
    0 -${thickness} 0px ${color},
    ${thickness} 0 0px ${color},
    0 ${thickness} 0px ${color};
`;
